import {useRef, Dispatch, SetStateAction, ChangeEvent} from 'react'
import cn from 'clsx'
import Dropzone from 'react-dropzone'
import {Space, Checkbox} from 'antd'

import styles from './DocumentsUpload.module.scss'
import {DOCUMENTS_COLLECTION, TDocumentsCollection} from 'modules/documents'
import {Text, Button, File, FilesIcon} from 'common/components'
import {useOpen} from 'common/hooks'
import {useUploadFiles} from 'common/api'
import {FileInterface} from 'common/types'

export enum OperateTypeEnum {
  ADD,
  REMOVE,
}
interface DocumentsUploadProps {
  checked?: boolean
  type: TDocumentsCollection
  files: FileInterface[]
  setFiles: Dispatch<SetStateAction<FileInterface[]>>
  isCreateDrawdown?: boolean
  handleFileChange?: (operateType: OperateTypeEnum) => void
}

interface UploadButtonProps {
  loading: boolean
  uploadFiles: any
}

export default function DocumentsUpload({
  checked,
  type,
  files,
  setFiles,
  isCreateDrawdown,
  handleFileChange
}: DocumentsUploadProps) {
  const documentsCollection = DOCUMENTS_COLLECTION[type]
  const {mutate: uploadFiles, isMutating: isUploadFileMutating} =
    useUploadFiles({
      key: type,
      isCreateDrawdown,
      setFiles: (files: FileInterface[]) => {
        setFiles((state) => [...state, ...files]);
        handleFileChange && handleFileChange(OperateTypeEnum.ADD);
      },
    })
  const [isDrag, handleDragStart, handleDragEnd] = useOpen()
  
  const handleRemove = (uuid: string) => {
    setFiles((state) => state.filter((file) => file.uuid !== uuid));
    handleFileChange && handleFileChange(OperateTypeEnum.REMOVE);
  }

  const isCheckbox = checked !== undefined

  const showDocuments = !isCheckbox || (isCheckbox && checked)

  return (
    <Dropzone
      noClick
      noKeyboard
      disabled={isCheckbox && !checked}
      onDrop={(files: any) => uploadFiles(files)}
      onDragEnter={handleDragStart}
      onDragLeave={handleDragEnd}
      onDropAccepted={handleDragEnd}
    >
      {({getRootProps, getInputProps}) => (
        <div
          {...getRootProps()}
          className={cn(
            styles.root,
            isDrag && styles.root_dragAccept,
            isCheckbox && styles.root_checkbox,
            checked && styles.root_checkboxChecked
          )}
        >
          <input {...getInputProps()} />
          <div className={styles.block}>
            {isCheckbox ? (
              <Checkbox value={type}>
                <Text as="h4" weight={null}>
                  {documentsCollection.label}
                </Text>
              </Checkbox>
            ) : (
              <Space>
                <FilesIcon />
                <Text as="h4" weight="demiBold">
                  {documentsCollection.label}
                </Text>
              </Space>
            )}
            <div>
              {showDocuments && (
                <UploadButton
                  loading={isUploadFileMutating}
                  uploadFiles={uploadFiles}
                />
              )}
            </div>
          </div>
          {showDocuments && Boolean(files.length) && (
            <ul>
              {files.map((file) => (
                <li key={file.uuid}>
                  <File
                    uuid={file.uuid}
                    name={file.blob.name}
                    size={file.blob.size}
                    url={file.url}
                    onRemove={handleRemove}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Dropzone>
  )
}

const UploadButton = ({loading, uploadFiles}: UploadButtonProps) => {
  const inputRef = useRef(null)

  const handleDownload = (e: ChangeEvent<HTMLInputElement>) => {
    uploadFiles(e.target.files)
    // clear the input value, there is no React specific way to do that
    setTimeout(() => {
      e.target.value = null
    })
  }

  const handleClick = () => inputRef.current.click()

  return (
    <>
      <Button variant="secondary" loading={loading} onClick={handleClick}>
        upload
      </Button>
      <input
        multiple
        className={styles.input}
        type="file"
        ref={inputRef}
        onChange={handleDownload}
      />
    </>
  )
}
